import { SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  TuiButton,
  TuiDialogContext,
  TuiHint,
  TuiIcon,
  TuiInput,
  TuiLoader,
  TuiScrollbar,
  TuiTextfield,
} from '@taiga-ui/core';
import { TranslatePipe } from '@ngx-translate/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  tap,
} from 'rxjs';

import { WorkflowService } from '@core/services/workflow/workflow.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  WorkflowEventsDialogTranslations,
  WorkflowEventsDialogTranslationsPayload,
} from '@core/types';
import {
  WorkflowEventsDialogData,
  WorkflowEventsState,
  WorkflowLogFilter,
} from '@workflows/types';
import {
  WorkflowLogFilterOption,
  WorkflowLogMapperLabels,
  WorkflowLogRow,
  WorkflowLogRowLabels,
} from './request-workflow-events-dialog.types';
import { WORKFLOW_LOG_FILTER_OPTIONS } from './workflow-log-ui.constants';

import {
  buildWorkflowLogRows,
  buildWorkflowLogStats,
  filterWorkflowLogRows,
  groupFileUploads,
  normalizeEvents,
  toTimestamp,
} from './workflow-log.mapper';
import { MoscowDayPipe } from './pipes/moscow-day.pipe';
import { MoscowTimePipe } from './pipes/moscow-time.pipe';

const DEFAULT_TRANSLATES: WorkflowEventsDialogTranslationsPayload = {
  [WorkflowEventsDialogTranslations.AllHeader]: 'Логи всех регламентов',
  [WorkflowEventsDialogTranslations.ErrorWorkflowNotFound]:
    'Регламент не найден.',
  [WorkflowEventsDialogTranslations.ErrorLoad]:
    'Не удалось загрузить события регламента.',
  [WorkflowEventsDialogTranslations.Files]: 'Файлов',
  [WorkflowEventsDialogTranslations.DefaultWorkflowLabel]: 'Регламент',
  [WorkflowEventsDialogTranslations.EventIdLabel]: 'ID события',
  [WorkflowEventsDialogTranslations.NoNameWorkflow]: 'Без названия',
  [WorkflowEventsDialogTranslations.OpenAllButton]: 'События всех регламентов',
  [WorkflowEventsDialogTranslations.SystemActorName]: 'Система',
  [WorkflowEventsDialogTranslations.DefaultEventLabel]: 'Событие',
  [WorkflowEventsDialogTranslations.DefaultEventStatus]: 'событие',
  [WorkflowEventsDialogTranslations.WorkflowCreatedLabel]: 'Регламент создан',
  [WorkflowEventsDialogTranslations.WorkflowCreatedStatus]: 'создано',
  [WorkflowEventsDialogTranslations.WorkflowInProgressLabel]:
    'Регламент открыт',
  [WorkflowEventsDialogTranslations.WorkflowInProgressStatus]: 'в работе',
  [WorkflowEventsDialogTranslations.WorkflowSubmittedLabel]:
    'Регламент отправлен',
  [WorkflowEventsDialogTranslations.WorkflowSubmittedStatus]: 'готово',
  [WorkflowEventsDialogTranslations.SubmissionInvalidLabel]:
    'Валидация не пройдена',
  [WorkflowEventsDialogTranslations.SubmissionInvalidStatus]: 'ошибка',
  [WorkflowEventsDialogTranslations.SubmissionSyncedLabel]:
    'Форма синхронизирована',
  [WorkflowEventsDialogTranslations.SubmissionNoChangesStatus]: 'без изменений',
  [WorkflowEventsDialogTranslations.SubmissionValidStatus]: 'валидно',
  [WorkflowEventsDialogTranslations.FileUploadedLabel]: 'Файл добавлен',
  [WorkflowEventsDialogTranslations.FileUploadedStatus]: 'загружено',
  [WorkflowEventsDialogTranslations.FileDeletedLabel]: 'Файл удален',
  [WorkflowEventsDialogTranslations.FileDeletedStatus]: 'удалено',
  [WorkflowEventsDialogTranslations.FileUploadGroupLabel]: 'Файлы добавлены',
};

const INITIAL_STATE: WorkflowEventsState = {
  status: 'loading',
  events: [],
};

@Component({
  selector: 'x-request-workflow-events-dialog',
  standalone: true,
  imports: [
    TuiButton,
    TuiLoader,
    TuiScrollbar,
    TuiIcon,
    TuiTextfield,
    TuiInput,
    ReactiveFormsModule,
    TuiHint,
    TranslatePipe,
    SlicePipe,
    MoscowTimePipe,
    MoscowDayPipe,
  ],
  templateUrl: './request-workflow-events-dialog.component.html',
  styleUrl: './request-workflow-events-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestWorkflowEventsDialogComponent {
  private readonly workflowService = inject(WorkflowService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
    void,
    WorkflowEventsDialogData
  >;

  protected readonly $translations = toSignal(
    this.outkeepTranslateService
      .translate(Object.values(WorkflowEventsDialogTranslations))
      .pipe(
        takeUntilDestroyed(),
        map(
          translates => translates as WorkflowEventsDialogTranslationsPayload,
        ),
      ),
    {
      initialValue:
        DEFAULT_TRANSLATES as WorkflowEventsDialogTranslationsPayload,
    },
  );

  protected readonly $allWorkflows = computed(
    () => this.context.data.allWorkflows ?? false,
  );
  protected readonly $headerTitle = computed(() =>
    this.$allWorkflows()
      ? this.t(WorkflowEventsDialogTranslations.AllHeader)
      : (
          this.context.data as Extract<
            WorkflowEventsDialogData,
            { allWorkflows?: false }
          >
        ).workflowName,
  );

  private readonly requestWorkflowId =
    'requestWorkflowId' in this.context.data
      ? this.context.data.requestWorkflowId
      : null;
  private readonly requestNumber = this.context.data.requestNumber;

  private readonly workflowLabels: Record<string, string> =
    this.$allWorkflows() || !this.requestWorkflowId
      ? {}
      : {
          [this.requestWorkflowId]: (
            this.context.data as Extract<
              WorkflowEventsDialogData,
              { allWorkflows?: false }
            >
          ).workflowName,
        };

  private readonly $events$ = this.$allWorkflows()
    ? this.workflowService.getEventsOfAllRequestWorkflows(this.requestNumber)
    : this.workflowService.getEventsOfRequestWorkflow(
        this.requestNumber,
        this.requestWorkflowId!,
      );

  protected readonly $state = toSignal(
    this.$events$.pipe(
      takeUntilDestroyed(),
      map(response => {
        if ('data' in response) {
          return {
            status: 'ready',
            events: [...response.data.events].sort(
              (left, right) =>
                toTimestamp(right.createdAt) - toTimestamp(left.createdAt),
            ),
          } satisfies WorkflowEventsState;
        }

        return {
          status: 'error',
          events: [],
          errorText:
            response.failureReason === 'requestWorkflowNotFound'
              ? this.t(WorkflowEventsDialogTranslations.ErrorWorkflowNotFound)
              : this.t(WorkflowEventsDialogTranslations.ErrorLoad),
        } satisfies WorkflowEventsState;
      }),
      catchError(() =>
        of<WorkflowEventsState>({
          status: 'error',
          events: [],
          errorText: this.t(WorkflowEventsDialogTranslations.ErrorLoad),
        }),
      ),
      startWith(INITIAL_STATE),
    ),
    { initialValue: INITIAL_STATE },
  );

  protected readonly $isLoading = computed(
    () => this.$state().status === 'loading',
  );

  protected readonly $errorText = computed(() =>
    this.$state().status === 'error' ? this.$state().errorText : '',
  );

  protected readonly $filter = signal<WorkflowLogFilter>(null);
  protected readonly $selectedWorkflow = signal<string | null>(null);
  protected readonly queryControl = new FormControl<string>('', {
    nonNullable: true,
  });
  protected readonly $query = toSignal(
    this.queryControl.valueChanges.pipe(
      startWith(this.queryControl.value),
      debounceTime(300),
      map(query => query.trim()),
      distinctUntilChanged(),
      tap(() => this.$expandedId.set(null)),
    ),
    { initialValue: '' },
  );
  protected readonly $hideQuiet = signal<boolean>(true);
  protected readonly $expandedId = signal<string | null>(null);

  protected readonly filterOptions: readonly WorkflowLogFilterOption[] =
    WORKFLOW_LOG_FILTER_OPTIONS;

  private readonly $processedEntries = computed(() =>
    this.$state().status === 'ready'
      ? groupFileUploads(
          normalizeEvents(this.$state().events, this.$mapperLabels()),
          15000,
        )
      : [],
  );

  private readonly $rowLabels = computed<WorkflowLogRowLabels>(() => ({
    defaultWorkflowLabel: this.t(
      WorkflowEventsDialogTranslations.DefaultWorkflowLabel,
    ),
    filesLabel: this.t(WorkflowEventsDialogTranslations.Files),
    eventIdLabel: this.t(WorkflowEventsDialogTranslations.EventIdLabel),
  }));

  private readonly $mapperLabels = computed<WorkflowLogMapperLabels>(() => ({
    systemActorName: this.t(WorkflowEventsDialogTranslations.SystemActorName),
    defaultEventLabel: this.t(
      WorkflowEventsDialogTranslations.DefaultEventLabel,
    ),
    defaultEventStatus: this.t(
      WorkflowEventsDialogTranslations.DefaultEventStatus,
    ),
    workflowCreatedLabel: this.t(
      WorkflowEventsDialogTranslations.WorkflowCreatedLabel,
    ),
    workflowCreatedStatus: this.t(
      WorkflowEventsDialogTranslations.WorkflowCreatedStatus,
    ),
    workflowInProgressLabel: this.t(
      WorkflowEventsDialogTranslations.WorkflowInProgressLabel,
    ),
    workflowInProgressStatus: this.t(
      WorkflowEventsDialogTranslations.WorkflowInProgressStatus,
    ),
    workflowSubmittedLabel: this.t(
      WorkflowEventsDialogTranslations.WorkflowSubmittedLabel,
    ),
    workflowSubmittedStatus: this.t(
      WorkflowEventsDialogTranslations.WorkflowSubmittedStatus,
    ),
    submissionInvalidLabel: this.t(
      WorkflowEventsDialogTranslations.SubmissionInvalidLabel,
    ),
    submissionInvalidStatus: this.t(
      WorkflowEventsDialogTranslations.SubmissionInvalidStatus,
    ),
    submissionSyncedLabel: this.t(
      WorkflowEventsDialogTranslations.SubmissionSyncedLabel,
    ),
    submissionNoChangesStatus: this.t(
      WorkflowEventsDialogTranslations.SubmissionNoChangesStatus,
    ),
    submissionValidStatus: this.t(
      WorkflowEventsDialogTranslations.SubmissionValidStatus,
    ),
    fileUploadedLabel: this.t(
      WorkflowEventsDialogTranslations.FileUploadedLabel,
    ),
    fileUploadedStatus: this.t(
      WorkflowEventsDialogTranslations.FileUploadedStatus,
    ),
    fileDeletedLabel: this.t(WorkflowEventsDialogTranslations.FileDeletedLabel),
    fileDeletedStatus: this.t(
      WorkflowEventsDialogTranslations.FileDeletedStatus,
    ),
    fileUploadGroupLabel: this.t(
      WorkflowEventsDialogTranslations.FileUploadGroupLabel,
    ),
  }));

  private readonly $rows = computed<WorkflowLogRow[]>(() =>
    buildWorkflowLogRows(
      this.$processedEntries(),
      this.$mapperLabels(),
      this.$rowLabels(),
      workflowId => this.getWorkflowLabel(workflowId),
    ),
  );

  protected readonly $visibleRows = computed<WorkflowLogRow[]>(() =>
    filterWorkflowLogRows(this.$rows(), {
      filter: this.$filter(),
      workflowId: this.$selectedWorkflow(),
      query: this.$query(),
      hideQuiet: this.$hideQuiet(),
    }),
  );

  protected readonly $stats = computed(() =>
    buildWorkflowLogStats(this.$visibleRows()),
  );

  protected readonly $workflowOptions = computed(() => {
    const ids = [
      ...new Set(this.$processedEntries().map(entry => entry.workflowId)),
    ];

    return ids.map(id => ({
      id,
      title: this.getWorkflowLabel(id) || id.slice(0, 8),
    }));
  });

  public close(): void {
    this.context.completeWith();
  }

  public setFilter(filter: WorkflowLogFilter): void {
    this.$filter.set(filter);
    this.$expandedId.set(null);
  }

  public setWorkflow(workflowId: string | null): void {
    this.$selectedWorkflow.set(workflowId);
    this.$expandedId.set(null);
  }

  public setHideQuiet(value: boolean): void {
    this.$hideQuiet.set(value);
    this.$expandedId.set(null);
  }

  public toggleEntry(entryId: string): void {
    this.$expandedId.update(current => (current === entryId ? null : entryId));
  }

  private getWorkflowLabel(workflowId: string): string {
    const fromMap = this.workflowLabels[workflowId];

    if (fromMap) {
      return fromMap;
    }

    const noNameLabel = this.t(WorkflowEventsDialogTranslations.NoNameWorkflow);
    const fromService = this.workflowService.getWorkFlowName(workflowId);

    if (fromService && fromService !== noNameLabel) {
      this.workflowLabels[workflowId] = fromService;

      return fromService;
    }

    return '';
  }

  private t(key: WorkflowEventsDialogTranslations): string {
    return this.$translations()[key] || DEFAULT_TRANSLATES[key];
  }
}
